<script setup>
import { ref, computed } from 'vue'
import { X } from 'lucide-vue-next'

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    casting: {
        type: Object,
        default: null,
    },
    availableStars: {
        type: Number,
        default: 0,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    error: {
        type: String,
        default: '',
    },
    success: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close', 'submit'])

const STAR_AMOUNTS = [1, 5, 10, 25, 50, 100]

const selectedAmount = ref(null)
const customAmount = ref('')
const givenAmount = ref(0)

const effectiveAmount = computed(() => {
    return customAmount.value ? parseInt(customAmount.value) || 0 : selectedAmount.value || 0
})

const submitDisabled = computed(() => {
    return effectiveAmount.value <= 0 || effectiveAmount.value > props.availableStars || props.loading
})

const insufficientBalance = computed(() => {
    return effectiveAmount.value > 0 && effectiveAmount.value > props.availableStars
})

const selectAmount = (amount) => {
    selectedAmount.value = amount
    customAmount.value = ''
}

const onCustomInput = () => {
    selectedAmount.value = null
}

const handleSubmit = () => {
    if (submitDisabled.value) return
    givenAmount.value = effectiveAmount.value
    emit('submit', effectiveAmount.value)
}

const handleClose = () => {
    selectedAmount.value = null
    customAmount.value = ''
    emit('close')
}

const giveMore = () => {
    selectedAmount.value = null
    customAmount.value = ''
    emit('resetSuccess')
}
</script>

<template>
    <Transition name="fade">
        <div
            v-if="isOpen"
            class="fixed inset-0 z-50 flex items-center justify-center p-4"
            style="background-color: rgba(0,0,0,0.85)"
            @click.self="handleClose"
        >
            <div class="relative w-full max-w-md bg-zinc-950 border border-white/[0.08] rounded-3xl shadow-2xl overflow-hidden">
                <!-- Close -->
                <button
                    @click="handleClose"
                    class="absolute top-4 right-4 z-10 p-1 rounded-full text-white/40 hover:text-white transition-colors hover:bg-white/10"
                >
                    <X class="w-5 h-5" />
                </button>

                <div class="px-6 py-6 pt-14">

                    <!-- Success state -->
                    <div v-if="success" class="flex flex-col items-center text-center">
                        <div class="w-16 h-16 rounded-full bg-green-500/10 border-2 border-green-500 flex items-center justify-center mb-4">
                            <svg class="w-8 h-8 stroke-green-500" fill="none" viewBox="0 0 24 24" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 class="text-green-400 text-2xl font-bold mb-2">Stars Given!</h2>
                        <p class="text-white/70 mb-1">
                            You gave <span class="text-amber-400 font-bold">{{ givenAmount }}</span> stars to support this casting request.
                        </p>
                        <p class="text-white/50 text-sm mb-8">The more support a casting gets, the better the chances it gets made.</p>

                        <button
                            @click="giveMore"
                            class="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-semibold py-3 rounded-xl mb-3 transition"
                        >
                            Give More Stars
                        </button>
                        <button @click="handleClose" class="text-white/40 hover:text-white/70 transition text-sm">
                            Close
                        </button>
                    </div>

                    <!-- Give stars form -->
                    <div v-else>
                        <!-- Header -->
                        <div class="flex items-start justify-between mb-6 gap-3">
                            <div>
                                <h2 class="text-white text-xl font-bold mb-0.5">Support Casting</h2>
                                <p class="text-white/40 text-xs">{{ casting?.title }}</p>
                            </div>
                            <div class="bg-zinc-800 rounded-2xl px-3 py-2.5 flex flex-col items-end flex-shrink-0">
                                <p class="text-white/40 text-[10px] uppercase tracking-wide mb-1">Your Balance</p>
                                <div class="flex items-center gap-1.5">
                                    <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-4 h-4" />
                                    <span class="text-white text-lg font-bold">{{ availableStars.toLocaleString() }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Preset amounts -->
                        <p class="text-white/40 text-xs mb-3">Choose an amount</p>
                        <div class="grid grid-cols-3 gap-3 mb-4">
                            <button
                                v-for="amount in STAR_AMOUNTS"
                                :key="amount"
                                @click="selectAmount(amount)"
                                :class="[
                                    'bg-zinc-800 hover:bg-zinc-700 rounded-2xl py-3 flex flex-col items-center justify-center transition border-2',
                                    selectedAmount === amount ? 'border-auditionsBlue' : 'border-transparent'
                                ]"
                            >
                                <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-5 h-5 mb-1" />
                                <span class="text-white font-bold text-sm">{{ amount }}</span>
                            </button>
                        </div>

                        <!-- Custom amount -->
                        <div class="mb-5">
                            <label class="text-white/40 text-sm mb-2 block">Or enter a custom amount</label>
                            <input
                                v-model="customAmount"
                                type="number"
                                min="1"
                                placeholder="Enter amount"
                                class="w-full bg-zinc-800 placeholder-zinc-600 text-white rounded-xl px-4 py-2.5 border border-zinc-700 focus:border-auditionsBlue focus:outline-none transition"
                                @input="onCustomInput"
                            />
                        </div>

                        <!-- Error / insufficient balance -->
                        <p v-if="insufficientBalance" class="text-red-400 text-sm text-center mb-3">
                            Insufficient balance. Choose a smaller amount.
                        </p>
                        <p v-else-if="error" class="text-red-400 text-sm text-center mb-3">{{ error }}</p>

                        <!-- Submit -->
                        <div class="flex gap-3">
                            <button
                                @click="handleSubmit"
                                :disabled="submitDisabled"
                                :class="[
                                    'flex-[2] font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2',
                                    submitDisabled ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed' : 'bg-auditionsBlue hover:bg-auditionsBlueHover text-white'
                                ]"
                            >
                                <svg v-if="loading" class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                </svg>
                                <span>{{ loading ? 'Processing…' : (effectiveAmount > 0 ? `Give ${effectiveAmount} Stars` : 'Select Amount') }}</span>
                            </button>
                            <button
                                @click="handleClose"
                                class="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-3 rounded-xl transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
input[type="number"] { -moz-appearance: textfield; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
